import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { FaSyncAlt } from 'react-icons/fa'
import api, { apiDownload } from '../../../../helpers/apiRequester'
import styles from '../shared/mhlShared.module.scss'
import { SourceTabs, StatTile } from '../shared/SourceTabs'
import { LeadsTable } from '../shared/LeadsTable'
import type { LeadSource, MhlLead } from '../shared/mhlTypes'
import { useMhlAccess } from '../shared/useMhlAccess'
import {
    filterAndSortMhlLeads,
    MHL_LEAD_FILTER_DEFAULTS,
    mhlChannelOptions,
    MhlLeadsFilter,
    type MhlLeadFilterState,
    type MhlReviewFilter,
} from '../MilitaryHearingLossLeadTracker/MhlLeadsFilter'

const REVIEW_FILTERS: MhlReviewFilter[] = ['all', 'awaiting_review', 'approved', 'rejected']

type ExportSource = 'dashboard' | 'webform'
type ExportEsign = 'link_not_sent' | 'signed' | 'unsigned'
type ExportReview = 'awaiting_review' | 'approved' | 'rejected'

const EXPORT_SOURCES: { value: ExportSource; label: string }[] = [
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'webform', label: 'Public webform' },
]

const EXPORT_ESIGN: { value: ExportEsign; label: string }[] = [
    { value: 'link_not_sent', label: 'Link not sent' },
    { value: 'signed', label: 'Signed' },
    { value: 'unsigned', label: 'Unsigned' },
]

const EXPORT_REVIEW: { value: ExportReview; label: string }[] = [
    { value: 'awaiting_review', label: 'Waiting' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
]

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)'

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/

const toggleValue = <T,>(current: readonly T[], value: T, on: boolean): T[] => {
    if (on) return current.includes(value) ? [...current] : [...current, value]
    return current.filter((item) => item !== value)
}

const selectedInOrder = <T extends string>(options: readonly { value: T }[], selected: readonly T[]) =>
    options.map((option) => option.value).filter((value) => selected.includes(value))

const realIsoDay = (value: string) => {
    const match = ISO_DAY.exec(value)
    if (!match) return false
    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    const date = new Date(Date.UTC(year, month - 1, day))
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

/** Client-side gate. The export route rejects the same invalid and reversed ranges. */
const dateRangeError = (fromLabel: string, toLabel: string, from: string, to: string) => {
    if (from && !realIsoDay(from)) return `${fromLabel} must be YYYY-MM-DD`
    if (to && !realIsoDay(to)) return `${toLabel} must be YYYY-MM-DD`
    if (from && to && from > to) return `${fromLabel} is after ${toLabel}`
    return null
}

const messageFromExportError = async (err: unknown) => {
    const responseData = (err as { response?: { data?: unknown }; message?: string })?.response?.data
    if (responseData instanceof Blob) {
        try {
            const parsed = JSON.parse(await responseData.text()) as { message?: unknown }
            if (typeof parsed?.message === 'string' && parsed.message) return parsed.message
        } catch {
            // Non-JSON error body.
        }
    } else if (responseData && typeof responseData === 'object' && 'message' in responseData) {
        const message = (responseData as { message?: unknown }).message
        if (typeof message === 'string' && message) return message
    }
    return (err as { message?: string })?.message || 'Export failed'
}

const saveExportFile = (blob: Blob, disposition: string, includeFiles: boolean) => {
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
    const fallbackDate = new Date().toISOString().slice(0, 10)
    const extension = includeFiles ? 'zip' : 'xlsx'
    const filename = filenameMatch?.[1] || `nihl-case-export-${fallbackDate}.${extension}`
    const blobUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = blobUrl
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(blobUrl)
}

/**
 * Cohort for the download. State lives here so opening the dialog does not copy
 * the Clients page source, search, or filters, and exporting does not change them.
 */
const MhlCaseExportDialog = ({ onClose }: { onClose: () => void }) => {
    const dialogRef = useRef<HTMLDivElement>(null)
    const alive = useRef(true)
    const [sources, setSources] = useState<ExportSource[]>(['dashboard', 'webform'])
    const [esignStates, setEsignStates] = useState<ExportEsign[]>(['signed'])
    const [reviewStatuses, setReviewStatuses] = useState<ExportReview[]>([])
    const [submittedFrom, setSubmittedFrom] = useState('')
    const [submittedTo, setSubmittedTo] = useState('')
    const [signedFrom, setSignedFrom] = useState('')
    const [signedTo, setSignedTo] = useState('')
    const [includeFiles, setIncludeFiles] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        alive.current = true
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
        dialogRef.current?.focus()
        return () => {
            alive.current = false
            document.body.style.overflow = previous
            previouslyFocused?.focus()
        }
    }, [])

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
                return
            }
            if (event.key !== 'Tab') return
            const node = dialogRef.current
            if (!node) return
            const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))
            if (!focusable.length) {
                event.preventDefault()
                return
            }
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            const active = document.activeElement
            if (event.shiftKey) {
                if (active === first || active === node) {
                    event.preventDefault()
                    last.focus()
                }
            } else if (active === last) {
                event.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    const clearError = () => setError(null)

    const runExport = async (event: FormEvent) => {
        event.preventDefault()
        if (downloading) return
        const dateError =
            dateRangeError('Submitted from', 'Submitted to', submittedFrom, submittedTo) ||
            dateRangeError('CFA signed from', 'CFA signed to', signedFrom, signedTo)
        if (dateError) {
            setError(dateError)
            return
        }

        const payload: Record<string, unknown> = {
            sources: selectedInOrder(EXPORT_SOURCES, sources),
            esignStates: selectedInOrder(EXPORT_ESIGN, esignStates),
            reviewStatuses: selectedInOrder(EXPORT_REVIEW, reviewStatuses),
            includeFiles,
        }
        if (submittedFrom) payload.submittedFrom = submittedFrom
        if (submittedTo) payload.submittedTo = submittedTo
        if (signedFrom) payload.signedFrom = signedFrom
        if (signedTo) payload.signedTo = signedTo

        setDownloading(true)
        setError(null)
        try {
            const response = await apiDownload('/military-hearing-loss-case-export', payload)
            if (!alive.current) return
            const disposition = String(response.headers['content-disposition'] || '')
            saveExportFile(response.data as Blob, disposition, includeFiles)
            onClose()
        } catch (err: unknown) {
            if (!alive.current) return
            setError(await messageFromExportError(err))
        } finally {
            if (alive.current) setDownloading(false)
        }
    }

    return createPortal(
        <div
            className={styles.exportOverlay}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <div
                ref={dialogRef}
                className={styles.exportDialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="mhl-case-export-title"
                aria-describedby="mhl-case-export-hint"
                aria-busy={downloading}
                tabIndex={-1}
            >
                <form className={styles.exportForm} onSubmit={(event) => void runExport(event)} noValidate>
                    <div className={styles.exportHeader}>
                        <div>
                            <h2 id="mhl-case-export-title" className={styles.exportTitle}>
                                Export cases
                            </h2>
                            <p id="mhl-case-export-hint" className={`${styles.hint} ${styles.exportIntro}`}>
                                These choices determine the data included in the export.
                            </p>
                        </div>
                        <button type="button" className={styles.exportClose} onClick={onClose} aria-label="Close">
                            ×
                        </button>
                    </div>
                    <div className={styles.exportBody}>
                        <fieldset className={styles.exportGroup} disabled={downloading}>
                            <legend>Source</legend>
                            <div className={styles.exportChecks}>
                                {EXPORT_SOURCES.map((option) => (
                                    <label key={option.value} className={styles.checkboxRow}>
                                        <input
                                            type="checkbox"
                                            checked={sources.includes(option.value)}
                                            onChange={(event) => {
                                                setSources((current) => toggleValue(current, option.value, event.target.checked))
                                                clearError()
                                            }}
                                        />
                                        {option.label}
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                        <fieldset className={styles.exportGroup} disabled={downloading}>
                            <legend>E-sign state</legend>
                            <div className={styles.exportChecks}>
                                {EXPORT_ESIGN.map((option) => (
                                    <label key={option.value} className={styles.checkboxRow}>
                                        <input
                                            type="checkbox"
                                            checked={esignStates.includes(option.value)}
                                            onChange={(event) => {
                                                setEsignStates((current) => toggleValue(current, option.value, event.target.checked))
                                                clearError()
                                            }}
                                        />
                                        {option.label}
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                        <fieldset className={styles.exportGroup} disabled={downloading}>
                            <legend>Review status</legend>
                            <div className={styles.exportChecks}>
                                {EXPORT_REVIEW.map((option) => (
                                    <label key={option.value} className={styles.checkboxRow}>
                                        <input
                                            type="checkbox"
                                            checked={reviewStatuses.includes(option.value)}
                                            onChange={(event) => {
                                                setReviewStatuses((current) => toggleValue(current, option.value, event.target.checked))
                                                clearError()
                                            }}
                                        />
                                        {option.label}
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                        <fieldset className={styles.exportGroup} disabled={downloading}>
                            <legend>Submitted date</legend>
                            <div className={styles.exportDates}>
                                <label className={styles.filterField}>
                                    <span className={styles.filterFieldLabel}>From</span>
                                    <input
                                        type="date"
                                        className={styles.dateInput}
                                        value={submittedFrom}
                                        onChange={(event) => {
                                            setSubmittedFrom(event.target.value)
                                            clearError()
                                        }}
                                    />
                                </label>
                                <label className={styles.filterField}>
                                    <span className={styles.filterFieldLabel}>To</span>
                                    <input
                                        type="date"
                                        className={styles.dateInput}
                                        value={submittedTo}
                                        onChange={(event) => {
                                            setSubmittedTo(event.target.value)
                                            clearError()
                                        }}
                                    />
                                </label>
                            </div>
                        </fieldset>
                        <fieldset className={styles.exportGroup} disabled={downloading}>
                            <legend>CFA signed date</legend>
                            <div className={styles.exportDates}>
                                <label className={styles.filterField}>
                                    <span className={styles.filterFieldLabel}>From</span>
                                    <input
                                        type="date"
                                        className={styles.dateInput}
                                        value={signedFrom}
                                        onChange={(event) => {
                                            setSignedFrom(event.target.value)
                                            clearError()
                                        }}
                                    />
                                </label>
                                <label className={styles.filterField}>
                                    <span className={styles.filterFieldLabel}>To</span>
                                    <input
                                        type="date"
                                        className={styles.dateInput}
                                        value={signedTo}
                                        onChange={(event) => {
                                            setSignedTo(event.target.value)
                                            clearError()
                                        }}
                                    />
                                </label>
                            </div>
                        </fieldset>
                        <fieldset className={styles.exportGroup} disabled={downloading}>
                            <legend>Files</legend>
                            <label className={styles.checkboxRow}>
                                <input
                                    type="checkbox"
                                    checked={includeFiles}
                                    onChange={(event) => {
                                        setIncludeFiles(event.target.checked)
                                        clearError()
                                    }}
                                />
                                Include case files (Audiogram and identity documents)
                            </label>
                        </fieldset>
                        {error && (
                            <div className={styles.errorBanner} role="alert">
                                {error}
                            </div>
                        )}
                    </div>
                    <div className={styles.exportFooter}>
                        <button type="button" className={styles.btn} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={downloading}>
                            {downloading ? 'Exporting…' : 'Export'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    )
}

export const MilitaryHearingLossLeads = () => {
    const { isMhlAdmin } = useMhlAccess()
    const [searchParams, setSearchParams] = useSearchParams()

    const initialSource = (searchParams.get('source') as LeadSource | null) || 'dashboard'
    const initialReview = (searchParams.get('review') as MhlReviewFilter | null) || 'all'

    const [source, setSource] = useState<LeadSource>(initialSource === 'webform' ? 'webform' : 'dashboard')
    const [filters, setFilters] = useState<MhlLeadFilterState>({
        ...MHL_LEAD_FILTER_DEFAULTS,
        review: REVIEW_FILTERS.includes(initialReview) ? initialReview : 'all',
    })
    const patchFilters = (partial: Partial<MhlLeadFilterState>) => setFilters((f) => ({ ...f, ...partial }))
    const [leads, setLeads] = useState<MhlLead[]>([])
    // Server-declared visibility: agents only receive their own / their team's clients.
    const [scope, setScope] = useState<{ all: boolean; teamName: string | null } | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [exportOpen, setExportOpen] = useState(false)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await api({ url: '/military-hearing-loss-leads-list', data: {} })
            if (res.data?.success) {
                setLeads(res.data.leads || [])
                setScope(res.data.scope ?? null)
                if (res.data.scope && !res.data.scope.all) setSource('dashboard')
            } else setError(res.data?.message || 'Failed to load leads')
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load leads')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    // Keep source/review in the URL so the reviewer notification email can deep
    // link straight into the awaiting-review queue.
    useEffect(() => {
        const next = new URLSearchParams()
        if (source !== 'dashboard') next.set('source', source)
        if (filters.review !== 'all') next.set('review', filters.review)
        setSearchParams(next, { replace: true })
    }, [source, filters.review, setSearchParams])

    const dashboardLeads = useMemo(() => leads.filter((l) => l.submitted_via === 'dashboard'), [leads])
    const webformLeads = useMemo(() => leads.filter((l) => l.submitted_via !== 'dashboard'), [leads])
    const awaitingCount = useMemo(() => dashboardLeads.filter((l) => l.review_status === 'awaiting_review').length, [dashboardLeads])

    const sourceLeads = source === 'dashboard' ? dashboardLeads : webformLeads
    const visible = useMemo(() => filterAndSortMhlLeads(sourceLeads, filters), [sourceLeads, filters])
    const channelOptions = useMemo(() => mhlChannelOptions(sourceLeads, source === 'webform'), [sourceLeads, source])

    const counts = useMemo(() => {
        const base = source === 'dashboard' ? dashboardLeads : webformLeads
        return {
            total: base.length,
            awaiting: base.filter((l) => l.review_status === 'awaiting_review').length,
            approved: base.filter((l) => l.review_status === 'approved').length,
            rejected: base.filter((l) => l.review_status === 'rejected').length,
            signed: base.filter((l) => l.esign.signed).length,
            linkSent: base.filter((l) => !l.esign.signed && l.esign.link_sent_at).length,
        }
    }, [source, dashboardLeads, webformLeads])

    return (
        <div className={styles.wrap}>
            {scope && !scope.all && (
                <p className={styles.hint} style={{ marginBottom: 14 }}>
                    {scope.teamName ? (
                        <>
                            Showing clients added by you and your team, <strong>{scope.teamName}</strong>.
                        </>
                    ) : (
                        <>Showing clients you have added. You’re not in a team yet — ask an MHL admin if you should be.</>
                    )}
                </p>
            )}
            {isMhlAdmin && awaitingCount > 0 && source === 'dashboard' && filters.review !== 'awaiting_review' && (
                <div className={styles.infoBanner}>
                    <span>
                        <strong>{awaitingCount}</strong> client{awaitingCount === 1 ? '' : 's'} awaiting your review before the e-sign link can be released.
                    </span>
                    <button type="button" className={`${styles.btn} ${styles.btnSmall}`} onClick={() => patchFilters({ review: 'awaiting_review' })}>
                        Show awaiting review
                    </button>
                </div>
            )}

            {/* Scoped agents only ever receive dashboard-onboarded clients, so the legacy tab is hidden. */}
            {(!scope || scope.all) && (
                <SourceTabs
                    ariaLabel="Lead source"
                    value={source}
                    onChange={(v) => {
                        setSource(v)
                        setFilters(MHL_LEAD_FILTER_DEFAULTS)
                    }}
                    options={[
                        { value: 'dashboard', label: 'Onboarded via dashboard', count: dashboardLeads.length },
                        { value: 'webform', label: 'Existing client base', count: webformLeads.length },
                    ]}
                />
            )}

            <div className={styles.statGrid}>
                <StatTile label="Clients" value={counts.total} />
                {source === 'dashboard' ? (
                    <>
                        <StatTile label="Awaiting review" value={counts.awaiting} tone={counts.awaiting ? 'warn' : undefined} onClick={() => patchFilters({ review: 'awaiting_review' })} />
                        <StatTile label="Approved" value={counts.approved} tone="good" onClick={() => patchFilters({ review: 'approved' })} />
                        <StatTile label="Rejected" value={counts.rejected} tone={counts.rejected ? 'bad' : undefined} onClick={() => patchFilters({ review: 'rejected' })} />
                    </>
                ) : (
                    <StatTile label="Link sent, unsigned" value={counts.linkSent} onClick={() => patchFilters({ esign: 'sent' })} />
                )}
                <StatTile label="CFA signed" value={counts.signed} tone="good" onClick={() => patchFilters({ esign: 'signed' })} />
            </div>

            <MhlLeadsFilter
                filters={filters}
                onChange={setFilters}
                matched={visible.length}
                total={sourceLeads.length}
                showReview={source === 'dashboard'}
                channelOptions={channelOptions}
            >
                <button type="button" className={`${styles.btn} ${styles.toolbarControl}`} onClick={() => void load()} disabled={loading} title="Refresh">
                    <FaSyncAlt size={11} /> Refresh
                </button>
                {isMhlAdmin && (
                    <button type="button" className={`${styles.btn} ${styles.toolbarControl}`} onClick={() => setExportOpen(true)}>
                        Export
                    </button>
                )}
            </MhlLeadsFilter>

            {error && <div className={styles.errorBanner}>{error}</div>}

            <LeadsTable
                leads={visible}
                loading={loading}
                showReview={source === 'dashboard'}
                showReferral={source === 'webform'}
                emptyMessage={
                    leads.length === 0
                        ? 'No leads yet.'
                        : source === 'dashboard' && dashboardLeads.length === 0
                          ? 'No clients have been onboarded through the dashboard yet.'
                          : 'No clients match the current filters.'
                }
            />
            {isMhlAdmin && exportOpen && <MhlCaseExportDialog onClose={() => setExportOpen(false)} />}
        </div>
    )
}
